import { Dispatch, useEffect, useReducer } from "react";

import { QuestionType } from "types/dataVoteType";
import { VoteStatus } from "types/voteEnum";
import {
    hasPersonAlreadyAnswered,
    isAnswerLocked,
    rollbackLockedVotes,
} from "components/vote/common/answerLock";
import {
    getAndCalculateCumulativeWeightPerDeliberation,
    getDeliberation,
} from "../services/reducerHelper";

export { VoteStatus };

export type Answer = {
    id: string;
    weight: number;
    disableOtherPropositionsIfSelected: boolean;
};

export type Deliberation = {
    idDeliberation: number;
    answers: Array<Answer>;
};

export type VoteStateGroupedDeliberation = {
    voterId: string;
    voteStatus: VoteStatus;
    deliberations: Array<Deliberation>;
};

type State = Array<VoteStateGroupedDeliberation>;

export type Person = {
    displayName: string;
    id: string;
    weight: number;
};

type Proposal = {
    id: string;
    disableOtherPropositionsIfSelected: boolean;
};

export type VoteStateAction =
    | {
          type: "updateWeightVote";
          idDeliberation: number;
          id: string;
          weight: number;
          voterId: string;
      }
    | { type: "sendAnswersRequest"; voterId: string }
    | { type: "sendAnswersSuccess"; voterId: string }
    | { type: "sendAnswersError"; voterId: string }
    | { type: "resetVote"; voterId: string }
    | { type: "resetAll" }
    | { type: "initializeVotes" }
    | {
          type: "replaceVoteForEveryParticipant";
          id: string;
          idDeliberation: number;
      };

type ReducerAction = Exclude<VoteStateAction, { type: "initializeVotes" }>;

function useVoteState(
    questions: Array<QuestionType>,
    proxies: Array<Person>,
    user: Person,
) {
    function reducer(state: State, action: ReducerAction): State {
        switch (action.type) {
            case "updateWeightVote": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        // Find the voter and deliberation that match the action data
                        const voter = [user, ...proxies].find(
                            (person) => person.id === vote.voterId,
                        ) as Person;

                        const deliberationMatch = getDeliberation(vote, action);

                        // Check if the current vote should disable other propositions
                        const currentVoteShouldDisabledOtherPropositions =
                            deliberationMatch?.answers.find(
                                (answer) => answer.id === action.id,
                            ) &&
                            /* Useful only in case of a vote with distribution
                               We don't want to disable other propositions if the weight is not the total weight */
                            action.weight === voter.weight;

                        // Update the answers in the deliberation

                        const newAnswers = deliberationMatch.answers.map(
                            (answer) => {
                                if (answer.id === action.id) {
                                    return { ...answer, weight: action.weight };
                                } else if (
                                    currentVoteShouldDisabledOtherPropositions
                                ) {
                                    return { ...answer, weight: 0 };
                                } else {
                                    return answer;
                                }
                            },
                        );

                        // Retrieve and update the answers of the current deliberation from the action
                        const newGroupedDeliberation = vote.deliberations.map(
                            (deliberation) => {
                                if (
                                    deliberation.idDeliberation ===
                                    action.idDeliberation
                                ) {
                                    return {
                                        ...deliberation,
                                        answers: newAnswers,
                                    };
                                } else {
                                    return deliberation;
                                }
                            },
                        );

                        const cumulativeWeightPerDeliberation =
                            getAndCalculateCumulativeWeightPerDeliberation(
                                newGroupedDeliberation,
                            );

                        // Update the vote status based on the cumulative weights
                        const newVoteStatus =
                            cumulativeWeightPerDeliberation.every(
                                (element) => element === voter.weight,
                            )
                                ? VoteStatus.PendingValidation
                                : VoteStatus.Idle;

                        // Return the updated vote object
                        return {
                            ...vote,
                            voteStatus: newVoteStatus,
                            deliberations: newGroupedDeliberation,
                        };
                    }),
                ];
            }
            case "sendAnswersRequest": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        return {
                            ...vote,
                            voteStatus: VoteStatus.Loading,
                        };
                    }),
                ];
            }
            case "sendAnswersSuccess": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        return {
                            ...vote,
                            voteStatus: VoteStatus.Success,
                        };
                    }),
                ];
            }
            case "sendAnswersError": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        return {
                            ...vote,
                            voteStatus: VoteStatus.Error,
                        };
                    }),
                ];
            }
            case "resetVote": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        const newDeliberation = vote.deliberations.map(
                            (deliberation) => {
                                return {
                                    ...deliberation,
                                    answers: createInitialAnswers(
                                        questions[
                                            deliberation.idDeliberation - 1
                                        ].propositions,
                                    ),
                                };
                            },
                        );

                        return {
                            ...vote,
                            deliberations: newDeliberation,
                            voteStatus: VoteStatus.Idle,
                        };
                    }),
                ];
            }
            case "resetAll": {
                return [
                    ...state.map((vote) => {
                        const newDeliberation = vote.deliberations.map(
                            (deliberation) => {
                                return {
                                    ...deliberation,
                                    answers: createInitialAnswers(
                                        questions[
                                            deliberation.idDeliberation - 1
                                        ].propositions,
                                    ),
                                };
                            },
                        );

                        return {
                            ...vote,
                            deliberations: newDeliberation,
                            voteStatus: VoteStatus.Idle,
                        };
                    }),
                ];
            }

            case "replaceVoteForEveryParticipant": {
                return state.map((vote) => {
                    const voter = [user, ...proxies].find(
                        (person) => person.id === vote.voterId,
                    ) as Person;

                    // Le vote du titulaire est déjà défini.
                    // On veut uniquement appliquer son vote aux procurations.
                    if (vote.voterId === user.id) {
                        return vote;
                    }

                    if (0 === voter.weight) {
                        return vote;
                    }

                    const newDeliberations = vote.deliberations.map(
                        (deliberation) => {
                            if (
                                !action.idDeliberation ||
                                deliberation.idDeliberation ===
                                    action.idDeliberation
                            ) {
                                const newAnswers = deliberation.answers.map(
                                    (answer) => ({
                                        ...answer,
                                        weight:
                                            answer.id === action.id
                                                ? voter.weight
                                                : 0,
                                    }),
                                );

                                return {
                                    ...deliberation,
                                    answers: newAnswers,
                                };
                            }
                            return deliberation;
                        },
                    );

                    const cumulativeWeightPerDeliberation =
                        getAndCalculateCumulativeWeightPerDeliberation(
                            newDeliberations,
                        );

                    const newVoteStatus = cumulativeWeightPerDeliberation.every(
                        (element) => element === voter.weight,
                    )
                        ? VoteStatus.PendingValidation
                        : VoteStatus.Idle;

                    return {
                        ...vote,
                        deliberations: newDeliberations,
                        voteStatus: newVoteStatus,
                    };
                });
            }
        }
    }

    function isVoteLocked(vote: VoteStateGroupedDeliberation): boolean {
        return isAnswerLocked(
            vote.voteStatus,
            hasPersonAnsweredAllGroupedQuestions(vote.voterId, questions),
            canGroupedAnswerBeUpdated(questions),
        );
    }

    // Single lock point: once a voter's answers are final, every change the
    // reducer computed for them is rolled back.
    function reducerWithAnswerLock(
        state: State,
        action: VoteStateAction,
    ): State {
        if ("initializeVotes" === action.type) {
            return createInitialState(questions, proxies, user);
        }

        return rollbackLockedVotes(state, reducer(state, action), isVoteLocked);
    }

    const [state, dispatch] = useReducer(
        reducerWithAnswerLock,
        createInitialState(questions, proxies, user),
    );

    const questionTitlesAsKey = questions.map((q) => q.title).join();

    // Keyed on THESE participants only: keying on every voter's answers would
    // wipe a vote in progress each time someone else votes.
    const answeredParticipantsKey = [user, ...proxies]
        .filter((person) =>
            hasPersonAnsweredAllGroupedQuestions(person.id, questions),
        )
        .map((person) => person.id)
        .join("|");

    useEffect(() => {
        // This check is performed to handle cases where a user reconnects
        // via a mobile socket without being notified of the closure of the
        // previous vote. We add question as dependency in order
        // to determine if the vote state needs to be updated. If the titles
        // has changed, it means the question has changed, and we need to
        // reset reducer state so as to reset voting state
        dispatch({ type: "initializeVotes" });
    }, [questionTitlesAsKey, answeredParticipantsKey]);

    return [state, dispatch] as [State, Dispatch<VoteStateAction>];
}

function createInitialAnswers(proposals: Array<Proposal>): Array<{
    id: string;
    weight: number;
    disableOtherPropositionsIfSelected: boolean;
}> {
    return proposals.map((proposal) => ({
        id: proposal.id,
        weight: 0,
        disableOtherPropositionsIfSelected:
            proposal.disableOtherPropositionsIfSelected,
    }));
}

export function canGroupedAnswerBeUpdated(
    questions: Array<QuestionType>,
): boolean {
    return questions.every((question) => question.canAnswerBeUpdated);
}

export function hasPersonAnsweredAllGroupedQuestions(
    personId: string,
    questions: Array<QuestionType>,
): boolean {
    return questions.every((question) =>
        hasPersonAlreadyAnswered(personId, question.answeredVoterIds),
    );
}

function createInitialState(
    questions: Array<QuestionType>,
    proxies: Array<Person>,
    user: Person,
): State {
    const deliberations = questions.map((question) => ({
        idDeliberation: question.id,
        answers: createInitialAnswers(question.propositions),
    }));

    return [user, ...proxies].map((person) => ({
        voterId: person.id,
        voteStatus: hasPersonAnsweredAllGroupedQuestions(person.id, questions)
            ? VoteStatus.Success
            : VoteStatus.Idle,
        deliberations: deliberations,
    }));
}

export default useVoteState;
